/**
 * Keys whose values must never reach Firestore. `error_log` is written under
 * anonymous auth, so anything stored there is readable by anyone who can reach
 * the collection: a credential in a log line is a public credential.
 *
 * `^current$` is listed explicitly because the change-password call posts
 * `{ current, password }` (profile.service.ts) and `current` — the old password —
 * matches none of the generic patterns.
 */
const SENSITIVE_KEY = /^(current|pin|otp)$|pass|secret|token|auth|credential|card|cvc|cvv|ssn|routing|accountnumber/i;

const REDACTED = '[redacted]';

/**
 * Hard cap on one serialised payload. Unbounded request bodies are what makes
 * the collection grow the way it does, and nothing past a few KB has ever been
 * useful for reading an error after the fact.
 */
export const ERROR_LOG_PAYLOAD_LIMIT = 8000;

/** Matches the Firestore TTL policy configured on `error_log.expireAt`. */
export const ERROR_LOG_RETENTION_DAYS = 90;

const redactValue = (value: any, depth: number): any => {
  if (depth > 6 || value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(item => redactValue(item, depth + 1));

  const out: Record<string, any> = {};
  for (const key of Object.keys(value))
    out[key] = SENSITIVE_KEY.test(key) ? REDACTED : redactValue(value[key], depth + 1);

  return out;
};

/**
 * Serialise a failed request's body for `error_log`, credentials stripped and
 * length capped. Returns '' for a bodyless request, matching what the writers
 * stored before.
 *
 * Redaction is by key name rather than by an allow-list on purpose: an
 * allow-list would throw away the shape that makes these logs worth reading.
 * The keys survive, so a reader can still tell which field was present.
 */
export const redactErrorLogPayload = (body: unknown): string => {
  if (body === null || body === undefined) return '';

  let serialised: string;
  try {
    serialised = JSON.stringify(redactValue(body, 0)) ?? '';
  } catch {
    // Circular or otherwise non-serialisable body. Say so instead of letting the
    // caller fall into its own catch and record nothing at all.
    return '[unserialisable payload]';
  }

  return serialised.length > ERROR_LOG_PAYLOAD_LIMIT
    ? `${serialised.slice(0, ERROR_LOG_PAYLOAD_LIMIT)}...[truncated]`
    : serialised;
};

/**
 * TTL marker read by the Firestore policy on `error_log.expireAt`.
 *
 * The stored field has to be a Firestore Timestamp: a TTL policy cannot be built
 * on `timestamp` (a number) or `createdAt` (a string), which is why nothing in
 * this collection has ever expired.
 *
 * Returns a plain Date and lets the SDK convert it, rather than importing
 * Timestamp from 'firebase/firestore'. A static import here would be pulled into
 * the initial bundle through the HTTP interceptor, and cf deliberately keeps that
 * ~290 KB chunk out of it with a dynamic import in firebase-crud.service.ts.
 */
export const errorLogExpiry = (fromMillis: number): Date =>
  new Date(fromMillis + ERROR_LOG_RETENTION_DAYS * 24 * 60 * 60 * 1000);

/**
 * Endpoints whose "failure" is an ordinary business state, by the status the API uses to
 * report it.
 *
 * Measured on mu over its 1000 most recent entries: /last-confirmed-order answering
 * "User has active subscription", /order/pre-order answering "No pre-order data" and
 * /on-demand-payment answering "User does not exist" were 65% of the whole collection.
 * /promotions and /account/subscriptions came from wh, pb and fmbx-tx, on much thinner
 * samples — worth revisiting once those collections hold days rather than hours.
 *
 * Deliberately NOT here: 404 on /producers/<slug> ("Producer not found"), which is a dead
 * link worth knowing about, and third-party hosts such as a.klaviyo.com, whose failures
 * reach this interceptor too.
 */
const BUSINESS_AS_USUAL: Record<number, string[]> = {
  400: [
    '/last-confirmed-order',    // "User has active subscription" / "User has active order"
    '/account/subscriptions',   // "You must have at least one subscription", retention coupons
  ],
  404: [
    '/login',
    '/order-history',
    '/subscriptions',
    '/last-confirmed-order',    // "User does not have confirmed orders"
    '/order/pre-order',         // "No pre-order data"
    '/on-demand-payment',       // "User does not exist"
    '/promotions',              // "Promotion does not exist"
  ],
};

export function preventErrorForLog(status: number, url: string | null): boolean {
  if (!url) return false;

  // Auth outcomes. The error handler owns these, and a 403 is how a dead session is
  // reported, so there is nothing to diagnose after the fact.
  if (status === 401 || status === 403) return true;

  // A suspended account opens its own modal from the interceptor. It is a state of the
  // account, not something that went wrong.
  if (status === 444) return true;

  return (BUSINESS_AS_USUAL[status] ?? []).some(path => url.indexOf(path) > -1);
}
